import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ExternalLink, ArrowUpRight, Home, BookOpen, Link2, Wrench, Shield, Trophy } from 'lucide-react';

/**
 * MainNavigation Component
 * Premium navigation that transforms on scroll
 */
export const MainNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  return (
    <>
      {/* Main Navigation */}
      <motion.nav className="py-4 sticky top-0 z-40">
        <motion.div
          animate={{
            maxWidth: isScrolled ? '1200px' : '1500px',
            margin: '0 auto',
            borderRadius: isScrolled ? '1rem' : '0.5rem',
            border: isScrolled ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.05)',
            padding: '1rem 2rem',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: isScrolled ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' : '0 4px 16px 0 rgba(0, 0, 0, 0.2)',
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="flex items-center justify-between relative mx-4 sm:mx-6 lg:mx-auto"
          style={{ marginLeft: undefined, marginRight: undefined }}
        >
          {/* Logo - Left on Mobile, Left on Desktop */}
          <motion.div
            animate={{
              scale: isScrolled ? 0.85 : 1,
              marginRight: isScrolled ? '1rem' : '2rem',
            }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center gap-2 group">
              <div className="font-bold flex items-baseline">
                <span className="text-primary text-2xl md:text-3xl">X</span>
                <span className="text-white text-xl md:text-2xl">treamGate</span>
              </div>
            </Link>
          </motion.div>

          {/* Navigation Items - Center */}
          <div className="hidden xl:flex items-center gap-8 flex-1 justify-center">
            <Link to="/" className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-all inline-flex items-center gap-2 group">
              <Home className="w-4 h-4 group-hover:scale-110 transition" />
              Home
            </Link>

            <Link to="/blog" className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-all inline-flex items-center gap-2 group">
              <BookOpen className="w-4 h-4 group-hover:scale-110 transition" />
              Blog
            </Link>

            <Link to="/sports" className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-all inline-flex items-center gap-2 group">
              <Trophy className="w-4 h-4 group-hover:scale-110 transition text-yellow-500" />
              Sports
              <motion.span
                animate={{ opacity: [1, 0.4, 1], scale: [1, 0.9, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-flex items-center gap-1 ml-1"
              >
                <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
              </motion.span>
            </Link>

            {/* Trusted Providers Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-all inline-flex items-center gap-2">
                <Link2 className="w-4 h-4 group-hover:scale-110 transition" />
                Trusted Providers
                <ChevronDown className="w-3 h-3 group-hover:rotate-180 transition" />
              </button>

              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-card border border-white/10 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                <div className="px-4 py-3">
                  <div className="space-y-2">
                    <Link to="/trusted-providers/xtreamcube" className="flex items-center justify-between px-3 py-2 rounded text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition group">
                      <span>XtreamCube (Official)</span>
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons - Right Side with Divider */}
          <div className="hidden xl:flex items-center gap-3 ml-4 pl-4 border-l border-white/10">
            <Link to="/tools" className="inline-flex px-5 py-2 rounded-lg text-sm font-semibold text-muted-foreground bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all duration-200 items-center gap-2">
              <Wrench className="w-4 h-4" />
              Free Tools
            </Link>

            <Link to="/trusted-providers/xtreamcube" className="inline-flex px-5 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_28px_rgba(16,185,129,0.6)] transition-all duration-200 items-center gap-2">
              <Shield className="w-4 h-4" />
              Trusted Provider
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={toggleMenu} className="xl:hidden p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/5 transition ml-auto">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </motion.div>

        {/* Mobile Menu - Uses same horizontal margins as header via wrapper */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="xl:hidden overflow-hidden"
              style={{ margin: '0.75rem 5%', width: '90%' }}
            >
              <div
                className="py-6 px-6 space-y-4 rounded-2xl glass"
                style={{ boxShadow: 'var(--shadow-glass)' }}
              >
                {/* Mobile Home */}
                <Link to="/" onClick={() => setIsOpen(false)} className="block px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-white hover:bg-white/10 transition text-center">
                  Home
                </Link>

                {/* Mobile Blog */}
                <Link to="/blog" onClick={() => setIsOpen(false)} className="block px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-white hover:bg-white/10 transition text-center">
                  Blog
                </Link>

                {/* Mobile Sports */}
                <Link to="/sports" onClick={() => setIsOpen(false)} className="block px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-white hover:bg-white/10 transition text-center flex items-center justify-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  Sports
                </Link>

                {/* Mobile Trusted Providers Dropdown */}
                <div>
                  <button onClick={() => toggleDropdown('links')} className="w-full px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition flex items-center justify-center gap-2">
                    Trusted Providers
                    <ChevronDown className={`w-4 h-4 transition ${openDropdown === 'links' ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {openDropdown === 'links' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-white/5 rounded-lg mt-2 overflow-hidden mx-2">
                        <div className="p-2 space-y-1">
                          <Link to="/trusted-providers/xtreamcube" onClick={() => setIsOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-muted-foreground hover:text-white transition">
                            <span>XtreamCube (Official)</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile CTA Buttons - Grouped at Bottom */}
                <div className="space-y-3 pt-5 border-t border-white/5 flex flex-col px-2 pb-2">
                  <Link
                    to="/tools"
                    onClick={() => setIsOpen(false)}
                    className="block w-full px-4 py-3 rounded-lg text-sm font-semibold text-white/90 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition text-center flex items-center justify-center gap-2"
                  >
                    <Wrench className="w-4 h-4" />
                    Free Tools
                  </Link>

                  <Link
                    to="/trusted-providers/xtreamcube"
                    onClick={() => setIsOpen(false)}
                    className="block w-full px-4 py-3 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 shadow-[0_4px_12px_rgba(16,185,129,0.25)] transition text-center flex items-center justify-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Trusted Provider
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default MainNavigation;
